import type { Fact, Hotspot, Journey, JourneyStop, RoleId } from '../types'

const ROLE_COPY: Record<RoleId, { label: string; summary: string }> = {
  spotter: { label: 'Spotter', summary: 'Find clues, wildlife, and landmarks.' },
  navigator: { label: 'Navigator', summary: 'Watch time, routes, and practical tips.' },
  storykeeper: { label: 'Storykeeper', summary: 'Share short place stories.' },
}

export interface RoamTogetherApi {
  openRoles: (
    onContinue: (rolesChosen: boolean) => void,
    onExplore?: () => void,
  ) => void
  openMission: (input: {
    stop: JourneyStop
    hotspot: Hotspot
    index: number
    total: number
    onConfirm: () => void
  }) => void
  openStop: (input: {
    journey: Journey
    stop: JourneyStop
    hotspot: Hotspot
    fact?: Fact
    index: number
    total: number
    /** Fires when the player commits — this is what plays the world reaction. */
    onReveal: (guess: { index: number; correct: boolean } | null) => void
    onPhoto?: () => void
    onContinue: (nextHotspotId?: string) => void
  }) => void
  openComplete: (input: {
    journey: Journey
    onSavePlan: () => void
    onPartner: () => void
    onDone: () => void
  }) => void
  close: () => void
  isOpen: () => boolean
}

export function bindRoamTogether(): RoamTogetherApi {
  const root = document.querySelector<HTMLElement>('#roam-together')!
  const kicker = document.querySelector<HTMLElement>('#rt-kicker')!
  const title = document.querySelector<HTMLElement>('#rt-title')!
  const body = document.querySelector<HTMLElement>('#rt-body')!
  const clue = document.querySelector<HTMLElement>('#rt-clue')!
  const questionBox = document.querySelector<HTMLElement>('#rt-question')!
  const questionPrompt = document.querySelector<HTMLElement>('#rt-question-prompt')!
  const questionHint = document.querySelector<HTMLElement>('#rt-question-hint')!
  const questionAnswer = document.querySelector<HTMLElement>('#rt-question-answer')!
  const roles = document.querySelector<HTMLElement>('#rt-roles')!
  const actions = document.querySelector<HTMLElement>('#rt-actions')!

  const close = () => {
    root.classList.add('hidden')
    root.classList.remove('mission')
    root.setAttribute('aria-hidden', 'true')
    root.inert = true
  }

  const open = (mission = false) => {
    root.classList.remove('hidden')
    root.classList.toggle('mission', mission)
    root.setAttribute('aria-modal', mission ? 'false' : 'true')
    root.removeAttribute('aria-hidden')
    root.inert = false
  }

  const clearActions = () => {
    actions.replaceChildren()
  }

  const addButton = (label: string, className: string, onClick: () => void) => {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = className
    button.textContent = label
    button.addEventListener('click', onClick)
    actions.append(button)
    return button
  }

  const openRoles = (
    onContinue: (rolesChosen: boolean) => void,
    onExplore?: () => void,
  ) => {
    kicker.textContent = 'Roam Together'
    title.textContent = 'Optional roles'
    body.textContent =
      'Play on one screen. Assign roles, fill them alone, or skip — same warm discovery loop for every persona.'
    clue.textContent = ''
    clue.hidden = true
    questionBox.hidden = true
    roles.hidden = false
    roles.replaceChildren()
    for (const id of Object.keys(ROLE_COPY) as RoleId[]) {
      const item = document.createElement('div')
      item.className = 'rt-role'
      item.innerHTML = `<strong>${ROLE_COPY[id].label}</strong><span>${ROLE_COPY[id].summary}</span>`
      roles.append(item)
    }
    clearActions()
    if (onExplore) {
      // Without this the card only leads deeper into the guided journey, and the
      // toolbar underneath it is unclickable.
      addButton('Explore on my own', 'secondary', () => {
        close()
        onExplore()
      })
    }
    addButton('Skip roles', 'secondary', () => {
      close()
      onContinue(false)
    })
    addButton('We’re ready', 'primary', () => {
      close()
      onContinue(true)
    })
    open()
  }

  const openMission = (input: {
    stop: JourneyStop
    hotspot: Hotspot
    index: number
    total: number
    onConfirm: () => void
  }) => {
    const role = input.stop.roleHint ? ROLE_COPY[input.stop.roleHint].label : 'Everyone'
    kicker.textContent = `Mission ${input.index + 1} / ${input.total} · ${role}`
    title.textContent = `Find ${input.hotspot.title}`
    body.textContent =
      input.stop.mission ?? `Locate ${input.hotspot.title} in the world and tap its gold pin.`
    clue.hidden = true
    clue.textContent = input.stop.clue ?? ''
    questionBox.hidden = true
    roles.hidden = true
    clearActions()
    addButton('Give me a hint', 'secondary', () => {
      clue.hidden = false
    })
    addButton('I found it', 'primary', input.onConfirm)
    open(true)
  }

  const openStop = (input: {
    journey: Journey
    stop: JourneyStop
    hotspot: Hotspot
    fact?: Fact
    index: number
    total: number
    onReveal: (guess: { index: number; correct: boolean } | null) => void
    onPhoto?: () => void
    onContinue: (nextHotspotId?: string) => void
  }) => {
    const role = input.stop.roleHint ? ROLE_COPY[input.stop.roleHint].label : 'Everyone'
    const question = input.stop.question
    title.textContent = input.hotspot.title
    roles.hidden = true

    const addNextActions = () => {
      if (input.stop.photoPrompt && input.onPhoto) {
        addButton('Photograph it', 'secondary', () => input.onPhoto?.())
      }
      const choices = input.stop.nextChoices ?? []
      if (input.index + 1 >= input.total) {
        addButton('Finish journey', 'primary', () => {
          close()
          input.onContinue()
        })
      } else if (choices.length > 0) {
        for (const choice of choices) {
          addButton(choice.label, 'primary route-choice', () => {
            close()
            input.onContinue(choice.hotspotId)
          })
        }
      } else {
        addButton('Continue the expedition', 'primary', () => {
          close()
          input.onContinue()
        })
      }
    }

    // Phase two: the world has answered, so show the fact, the reaction and the
    // verdict on the guess.
    const showReveal = (guess: { index: number; correct: boolean } | null) => {
      kicker.textContent = `Discovery ${input.index + 1} / ${input.total} · ${role}`
      body.textContent = input.fact?.plainAnswer ?? input.journey.summary
      const aside = input.stop.reaction ?? input.stop.clue ?? ''
      clue.hidden = aside === ''
      clue.textContent = aside
      if (question) {
        questionAnswer.hidden = false
        const verdict = guess ? (guess.correct ? 'You called it.' : 'Not quite.') : ''
        questionAnswer.textContent = verdict
          ? `${verdict} ${question.answer}`
          : question.answer
        questionAnswer.classList.toggle('missed', guess?.correct === false)
        questionHint.hidden = true
      }
      input.onReveal(guess)
    }

    clearActions()

    if (!question) {
      kicker.textContent = `Discovery ${input.index + 1} / ${input.total} · ${role}`
      questionBox.hidden = true
      showReveal(null)
      addNextActions()
      open()
      return
    }

    // Phase one: withhold the fact and the reaction so the guess is a real
    // prediction rather than a recall check.
    kicker.textContent = `Prediction ${input.index + 1} / ${input.total} · ${role}`
    body.textContent = 'You found it. Call it before Arenal shows you.'
    clue.hidden = true
    clue.textContent = ''
    questionBox.hidden = false
    questionPrompt.textContent = question.prompt
    questionHint.hidden = false
    questionHint.textContent = `Hint: ${question.hint}`
    questionAnswer.hidden = true
    questionAnswer.classList.remove('missed')

    const options = question.options
    if (options?.length) {
      const optionButtons = options.map((option, optionIndex) =>
        addButton(option, 'answer-option', () => {
          for (const button of optionButtons) button.disabled = true
          const correct = optionIndex === question.correctIndex
          optionButtons[optionIndex]?.classList.add(correct ? 'correct' : 'incorrect')
          optionButtons[question.correctIndex ?? -1]?.classList.add('correct')
          showReveal({ index: optionIndex, correct })
          addNextActions()
        }),
      )
    } else {
      addButton('Lock in my guess', 'secondary', () => {
        clearActions()
        showReveal(null)
        addNextActions()
      })
    }
    open()
  }

  const openComplete = (input: {
    journey: Journey
    onSavePlan: () => void
    onPartner: () => void
    onDone: () => void
  }) => {
    kicker.textContent = 'Stamp earned'
    title.textContent = input.journey.stamp
    body.textContent = `${input.journey.title} complete. Save a day plan or continue to a trusted next step.`
    clue.hidden = false
    clue.textContent = 'Same Roam Together loop — your persona only changed the route.'
    questionBox.hidden = true
    roles.hidden = true
    clearActions()
    addButton('Save day plan', 'secondary', () => {
      input.onSavePlan()
    })
    if (input.journey.partnerAction) {
      addButton(input.journey.partnerAction.label, 'primary', () => {
        input.onPartner()
      })
    }
    addButton('Keep exploring', 'secondary', () => {
      close()
      input.onDone()
    })
    open()
  }

  close()
  return {
    openRoles,
    openMission,
    openStop,
    openComplete,
    close,
    isOpen: () => !root.classList.contains('hidden'),
  }
}
